/**
 * Promotion tracking resolution for the Storyblok CTA Button (ECOM-2766).
 *
 * A CTA nested in a parent block that already emits promotion events inherits
 * that parent's `promotion_id` / `promotion_name`; a standalone CTA uses its
 * own configured parameters.
 */

/**
 * Published by a parent block that already emits promotion events for the CTA
 * it nests. Presence is the claim; `claimedBy` only says which block owns them.
 */
export type CtaPromotionContext = {
  claimedBy: string
}

/**
 * Promotion fields as configured on a CTA Button blok. All optional: the AGS
 * space (160132) has all three, the MAD space (265080) has none of them yet.
 */
export type CtaPromotionFields = {
  promotion_id?: string
  promotion_name?: string
  creative_name?: string
}

/** Structurally matches the app-level `TrackPromotionParams`. */
export type CtaPromotionParams = {
  id: string
  name: string
  promoType: string
  position: number
  creativeNameSb: string
}

export const CTA_PROMO_TYPE = 'ctabutton'

export const resolveCtaPromotion = ({
  inherited,
  blok,
  position = 1,
}: {
  inherited?: CtaPromotionContext
  // Widened past `CtaPromotionFields` on purpose. MAD's generated CtaButton type
  // carries none of those fields, and TypeScript rejects a type that shares no
  // property with an all-optional one, so naming the narrow type here would make
  // MAD's call site a type error. Reading through the cast below instead keeps
  // both brands cast-free and lets MAD start tracking with no code change once
  // its Storyblok schema gains the fields.
  blok?: object | null
  position?: number
}): CtaPromotionParams | null => {
  // The claiming parent emits the events with its own parameters, so the CTA
  // staying silent is what makes GA4 show inherited ids.
  if (inherited) {
    return null
  }

  const fields = blok as CtaPromotionFields | null | undefined

  const id = fields?.promotion_id
  // An unconfigured CTA sends nothing rather than a placeholder id: a CTA can
  // sit in any rich text, so fallback ids would swamp GA4.
  if (!id) {
    return null
  }

  return {
    id,
    name: fields?.promotion_name || id,
    promoType: CTA_PROMO_TYPE,
    position,
    creativeNameSb: fields?.creative_name || '',
  }
}
